import get from "lodash/get";
import type { Company, Flag, Monitor } from "@kanda-libs/ks-frontend-services";
import { SORTING, ORDER } from "components/company/CompanyTable/constants";

export const formatFlag = (flag?: Flag): string => {
  const level = flag?.level || "green";

  return `flag:${level}`;
};

export interface FormattedCompany {
  id?: string;
  companyName?: string;
  companyType?: string;
  companyFlag?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  directorEmails?: string;
  directorPhones?: string;
  lifecycle?: string;
  referral?: string;
  type?: string;
  companyNumber?: string;
}

export const formatCompanies = (
  companies: Company[],
  completedSetup: boolean,
  monitors: Monitor[]
): FormattedCompany[] =>
  companies
    .filter(
      (company) => !completedSetup || company?.lifecycle === "completed_setup"
    )
    .map((company) => {
      const companyMonitors = monitors.find(({ id }) => id === company?.id);

      return {
        id: company?.cid,
        companyName:
          company?.limited_company_info?.company_name ||
          company?.sole_trader_info?.trading_name,
        companyType: company?.company_type,
        companyFlag: formatFlag(companyMonitors?.flag),
        contactName: company?.contact_info?.contact_name || "No contact name",
        contactEmail:
          company?.contact_info?.contact_email || "No contact email",
        contactPhone:
          company?.contact_info?.contact_phone || "No contact phone",
        directorEmails:
          company?.users?.map(({ email }) => email).join(", ") ||
          "No director emails",
        directorPhones:
          company?.users?.map(({ mobile }) => mobile).join(", ") ||
          "No director phones",
        lifecycle: company?.lifecycle,
        referral: company?.referral_code || "--",
        type: company?.referral_code === "SOLAR1" ? "Solar" : "Standard",
        companyNumber:
          company?.company_type === "limited_company"
            ? company?.limited_company_info?.company_number
                ?.toUpperCase()
                ?.replace(/ /g, "") || "No company number"
            : company.sole_trader_info?.national_insurance_number
                ?.toUpperCase()
                ?.replace(/ /g, "") || "No NI number",
      };
    });

/**
 * Sorts jobs by price
 */
const sortByName = (
  company: FormattedCompany[],
  accessor: string,
  order: string
) =>
  company.sort((company1, company2) => {
    const name1 = get(company1, accessor);
    const name2 = get(company2, accessor);
    if (order === ORDER.INVERSE) return name2.localeCompare(name1);
    return name1.localeCompare(name2);
  });

/**
 * Sorts jobs according to sorting and order
 */
export const sortData = (
  companies: FormattedCompany[],
  sorting: string,
  order: string
): FormattedCompany[] | null => {
  if (!companies) return null;
  // If no jobs, return null to show placeholder
  if (companies.length === 0) return [];

  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.ID) return sortByName(companies, "id", order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.COMPANY_NAME)
    return sortByName(companies, "companyName", order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.COMPANY_TYPE)
    return sortByName(companies, "companyType", order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.CONTACT_NAME)
    return sortByName(companies, "contactName", order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.CONTACT_EMAIL)
    return sortByName(companies, "contactEmail", order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.LIFECYCLE)
    return sortByName(companies, "lifecycle", order);
  // If sorting is flag, return data sorted by flag
  if (sorting === SORTING.FLAG)
    return sortByName(companies, "companyFlag", order);

  return companies;
};
